import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { Seo } from "@/components/Seo";
import { InnerBlock } from "@/components/InnerBlock";
import { InnerCTA } from "@/components/InnerCTA";
import NotFound from "@/pages/NotFound";
import { BLOG_POSTS } from "@/data/blog";
import { getBlogImage } from "@/data/blog-images";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  } catch (_) {
    return iso;
  }
}

/**
 * Render a paragraph with inline **bold** turned into <strong>. Kept small and
 * deliberate — we do not want a full markdown parser here.
 */
function InlineText({ text }) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((p, i) =>
        p.startsWith("**") && p.endsWith("**") ? (
          <strong key={i} className="font-semibold text-wafa-deep">{p.slice(2, -2)}</strong>
        ) : (
          <span key={i}>{p}</span>
        ),
      )}
    </>
  );
}

export default function BlogPost() {
  const { slug } = useParams();
  const post = BLOG_POSTS.find((p) => p.slug === slug);
  if (!post) return <NotFound />;

  // FAQPage JSON-LD for search engines.
  const faqJsonLd = post.faq?.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: post.faq.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }
    : null;

  return (
    <div data-testid={`blog-post-${post.slug}`}>
      <Seo title={post.meta?.title || post.title} description={post.meta?.description || post.dek} />
      {faqJsonLd ? (
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
        />
      ) : null}

      {/* Post header */}
      <section className="bg-wafa-cream/40">
        <div className="mx-auto max-w-3xl px-4 py-16 lg:px-6 lg:py-24">
          <nav className="mb-6 text-xs text-wafa-ink/60" aria-label="Breadcrumb">
            <ol className="flex flex-wrap items-center gap-1">
              <li><Link to="/" className="hover:text-wafa-deep">Home</Link><span className="px-1">·</span></li>
              <li><Link to="/resources" className="hover:text-wafa-deep">Resources</Link><span className="px-1">·</span></li>
              <li><Link to="/resources/blog" className="hover:text-wafa-deep">Blog</Link></li>
            </ol>
          </nav>
          <div className="text-xs font-semibold uppercase tracking-widest text-wafa-primary">{post.category || post.tag}</div>
          <h1 className="mt-4 font-heading text-3xl font-semibold leading-tight tracking-tight text-wafa-deep sm:text-4xl lg:text-5xl">
            {post.title}
          </h1>
          <p className="mt-5 text-base leading-relaxed text-wafa-ink/85 lg:text-lg">{post.dek}</p>
          <div className="mt-6 flex flex-wrap items-center gap-4 text-xs text-wafa-ink/60">
            <span>{post.author}</span>
            <span>·</span>
            <span>{formatDate(post.date)}</span>
            <span>·</span>
            <span>{post.readTime}</span>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="bg-white">
        <div className="mx-auto max-w-3xl px-4 py-16 lg:px-6 lg:py-24">
          {/* Post hero image — omitted for posts that do not have one on
              file. Single-candidate WebP with a JPG fallback; do not add
              a second -srcset arm without a matching file on disk (see the
              SPA catch-all note in /data/blog-images.js). */}
          {(() => {
            const hero = getBlogImage(post.slug);
            if (!hero) return null;
            return (
              <div className="mb-10 overflow-hidden rounded-lg bg-wafa-cream/30">
                <picture>
                  <source type="image/webp" srcSet={hero.webp} />
                  <img
                    src={hero.jpg}
                    alt={hero.alt}
                    width={hero.width}
                    height={hero.height}
                    loading="lazy"
                    decoding="async"
                    data-testid={`blog-hero-${post.slug}`}
                    className="block h-auto w-full object-cover"
                  />
                </picture>
              </div>
            );
          })()}

          {/* "In short —" summary box */}
          {post.inShort?.length ? (
            <aside
              data-testid="blog-in-short"
              className="mb-10 rounded-lg border border-wafa-primary/25 bg-wafa-cream/40 p-5 lg:p-6"
            >
              <div className="text-xs font-semibold uppercase tracking-widest text-wafa-primary">In short —</div>
              <ul className="mt-3 space-y-2">
                {post.inShort.map((b, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-wafa-deep lg:text-base">
                    <Check className="mt-1 h-4 w-4 shrink-0 text-wafa-primary" />
                    <InlineText text={b} />
                  </li>
                ))}
              </ul>
            </aside>
          ) : null}

          {/* Lead paragraphs */}
          {post.lead?.length ? (
            <div className="space-y-5 text-base leading-relaxed text-wafa-ink/85 lg:text-lg">
              {post.lead.map((p, i) => (
                <p key={i}><InlineText text={p} /></p>
              ))}
            </div>
          ) : null}

          {/* H2 sections */}
          {post.sections?.map((s, i) => (
            <div key={i} className="mt-12">
              <h2 className="font-heading text-2xl font-semibold tracking-tight text-wafa-deep sm:text-3xl">{s.h2}</h2>
              <div className="mt-5 space-y-5 text-base leading-relaxed text-wafa-ink/85 lg:text-lg">
                {s.paragraphs.map((p, j) => (
                  <p key={j}><InlineText text={p} /></p>
                ))}
              </div>
            </div>
          ))}

          {/* FAQ */}
          {post.faq?.length ? (
            <div className="mt-14 rounded-lg border border-wafa-deep/10 bg-wafa-cream/30 p-6 lg:p-8">
              <h2 className="font-heading text-xl font-semibold tracking-tight text-wafa-deep sm:text-2xl">
                Frequently asked
              </h2>
              <dl className="mt-6 space-y-5">
                {post.faq.map((f, i) => (
                  <div key={i}>
                    <dt className="font-semibold text-wafa-deep">{f.q}</dt>
                    <dd className="mt-1 text-sm text-wafa-ink/80 lg:text-base">{f.a}</dd>
                  </div>
                ))}
              </dl>
            </div>
          ) : null}

          {/* Internal links */}
          {post.internalLinks?.length ? (
            <div className="mt-14">
              <div className="text-xs font-semibold uppercase tracking-widest text-wafa-primary">Read next</div>
              <ul className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {post.internalLinks.map((l, i) => (
                  <li key={i}>
                    <Link
                      to={l.to}
                      data-testid={`blog-internal-${i}`}
                      className="group flex items-center justify-between rounded-lg border border-wafa-deep/10 bg-white p-4 text-sm font-semibold text-wafa-deep transition-all hover:-translate-y-0.5 hover:border-wafa-deep/25"
                    >
                      <span>{l.label}</span>
                      <ArrowRight className="h-4 w-4 shrink-0 text-wafa-primary transition-transform group-hover:translate-x-1" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          <div className="mt-14">
            <Link
              to="/resources/blog"
              data-testid="blog-back"
              className="inline-flex items-center gap-2 text-sm font-semibold text-wafa-deep hover:text-wafa-primary"
            >
              <ArrowLeft className="h-4 w-4" /> All posts
            </Link>
          </div>
        </div>
      </section>

      <InnerCTA
        heading="Talk to the team behind this."
        body="If any of this touches a decision you are making, book a thirty-minute call — an engineer will listen and send you a one-page brief."
      />
    </div>
  );
}
